import { emitDocumentPreviewOpenRequested } from '@/features/documentPreview/documentPreviewEvents'
import { registerArtifactPayload } from '@/features/documentPreview/artifactPayloadStore'
import {
  DESIGN_DNA_DEMO_ARTIFACT_ID,
  DESIGN_DNA_HTML_ARTIFACT_ID,
  ensureDesignDnaDemoArtifactsRegistered,
} from '../demo/designDnaDemo.js'
import {
  DESIGN_DNA_PHASE1_THOUGHT_DETAILS,
  DESIGN_DNA_PLAN_WORKED_LABEL,
  buildDesignDnaArtifactPayload,
  buildDesignDnaHtmlArtifactPayload,
  buildDesignDnaPlanBlocks,
} from '../demo/designDnaDemoContent.js'
import { computeStreamTypingDuration } from '../streaming/computeStreamTypingDuration.js'
import {
  randomPreamblePauseMs,
  scaleChatStreamContentDelay,
  scaleChatStreamDelay,
} from '../streaming/chatStreamSpeed.js'
import { runTypewriter } from '../streaming/runTypewriter.js'
import { runWorkflowStepsInStream } from './runWorkflowStepsInStream.js'
import { createDemoSelectionController } from '../demo/demoSelectionSwitch.js'
import { updatePendingDesignDnaDemoMessage } from './patchDesignDnaDemoMessage.js'

export function startDesignDnaDemoStreamLoop(ctx) {
  const { tid, pendingId, signal, session, setMessagesByThreadId } = ctx

  ensureDesignDnaDemoArtifactsRegistered()

  const state = {
    thoughtDetails: /** @type {string[]} */ ([]),
    blocks: /** @type {object[]} */ ([]),
    dnaArtifactId: DESIGN_DNA_DEMO_ARTIFACT_ID,
    htmlArtifactId: DESIGN_DNA_HTML_ARTIFACT_ID,
  }

  /** @type {string | null} */
  let workedSummary = null

  const patch = (patchFields) => {
    if (workedSummary) {
      patchFields = { thoughtSummary: workedSummary, ...patchFields }
    }
    updatePendingDesignDnaDemoMessage(setMessagesByThreadId, tid, pendingId, patchFields)
  }

  const selection = createDemoSelectionController(state, patch)

  const schedule = (ms, fn) => {
    session.addTimeout(() => {
      if (signal.aborted) {
        session.dispose()
        return
      }
      fn()
    }, ms)
  }

  const runAfterContent = (ms, fn) => schedule(scaleChatStreamContentDelay(ms), fn)

  const runTypewriterThought = (text, onUpdate, onDone) => {
    runTypewriter({
      session,
      signal,
      text,
      durationMs: scaleChatStreamDelay(
        computeStreamTypingDuration(text.length, { min: 1000, max: 2000 }),
      ),
      onUpdate,
      onDone,
    })
  }

  const runTypewriterContent = (text, onUpdate, onDone) => {
    runTypewriter({
      session,
      signal,
      text,
      durationMs: scaleChatStreamContentDelay(computeStreamTypingDuration(text.length)),
      onUpdate,
      onDone,
    })
  }

  const runPreambleThinking = (onDone) => {
    patch({
      demoStreamActive: true,
      thoughtSummary: 'Thinking…',
      thoughtDetails: [],
      blocks: [],
      agentBrand: 'SIA',
      agentName: 'Master Root Agent',
    })
    selection.setInitial({
      agentId: 'design-specialist',
      personaId: 'architect',
    })
    schedule(randomPreamblePauseMs(), onDone)
  }

  const runThoughtParagraphs = (onDone) => {
    let paraIdx = 0

    const runNextParagraph = () => {
      if (paraIdx >= DESIGN_DNA_PHASE1_THOUGHT_DETAILS.length) {
        workedSummary = DESIGN_DNA_PLAN_WORKED_LABEL
        patch({ thoughtSummary: workedSummary, thoughtDetails: [...state.thoughtDetails] })
        schedule(randomPreamblePauseMs(), onDone)
        return
      }

      const fullText = DESIGN_DNA_PHASE1_THOUGHT_DETAILS[paraIdx]
      state.thoughtDetails.push('')
      const currentParaIndex = state.thoughtDetails.length - 1
      patch({ thoughtDetails: [...state.thoughtDetails] })

      runTypewriterThought(
        fullText,
        (partial) => {
          state.thoughtDetails[currentParaIndex] = partial
          patch({ thoughtDetails: [...state.thoughtDetails] })
        },
        () => {
          paraIdx += 1
          schedule(randomPreamblePauseMs(), runNextParagraph)
        },
      )
    }

    runNextParagraph()
  }

  const runHeadingType = (level, content, onDone) => {
    const blockIndex = state.blocks.length
    state.blocks.push({ type: 'heading', level, content: '' })
    patch({ blocks: [...state.blocks] })
    runTypewriterContent(
      content,
      (partial) => {
        state.blocks[blockIndex] = { type: 'heading', level, content: partial }
        patch({ blocks: [...state.blocks] })
      },
      onDone,
    )
  }

  const runMarkdownType = (content, variant, onDone) => {
    const blockIndex = state.blocks.length
    state.blocks.push({ type: 'markdown', content: '', variant })
    patch({ blocks: [...state.blocks] })
    runTypewriterContent(
      content,
      (partial) => {
        state.blocks[blockIndex] = { type: 'markdown', content: partial, variant }
        patch({ blocks: [...state.blocks] })
      },
      onDone,
    )
  }

  const runWorkflowSteps = (templates, onDone) => {
    const logIndex = state.blocks.length
    state.blocks.push({ type: 'aiResponseLog', status: 'ok', showLogStatus: false, steps: [] })
    patch({ blocks: [...state.blocks] })

    const patchLog = (steps) => {
      state.blocks[logIndex] = {
        type: 'aiResponseLog',
        status: 'ok',
        showLogStatus: false,
        steps,
      }
      patch({ blocks: [...state.blocks] })
    }

    runWorkflowStepsInStream({
      templates,
      session,
      signal,
      runAfter: schedule,
      onStepsChange: patchLog,
      onDone,
    })
  }

  const registerFileArtifact = (block) => {
    registerArtifactPayload({
      artifactId: block.artifactId,
      kind: 'doc',
      fileName: block.fileName,
      description: block.description,
      payload: buildDesignDnaArtifactPayload(block.artifactId),
    })
  }

  const runBlocks = (blocks, onDone) => {
    let i = 0

    const addNext = () => {
      if (i >= blocks.length) {
        patch({
          demoStreamActive: false,
          thoughtSummary: DESIGN_DNA_PLAN_WORKED_LABEL,
          designDnaExtractionCompleted: true,
          designDnaAwaitingProceed: true,
          blocks: [...state.blocks],
        })
        session.dispose()
        onDone?.()
        return
      }

      const block = blocks[i]

      if (block.type === 'markdown' && typeof block.content === 'string') {
        runMarkdownType(block.content, block.variant, () => {
          i += 1
          runAfterContent(180, addNext)
        })
        return
      }

      if (block.type === 'heading' && typeof block.content === 'string') {
        runHeadingType(block.level, block.content, () => {
          i += 1
          runAfterContent(180, addNext)
        })
        return
      }

      if (block.type === 'aiResponseLog' && Array.isArray(block.stepTemplates)) {
        runWorkflowSteps(block.stepTemplates, () => {
          i += 1
          runAfterContent(180, addNext)
        })
        return
      }

      if (block.type === 'fileArtifact') {
        registerFileArtifact(block)
      }

      state.blocks.push(block)
      patch({ blocks: [...state.blocks] })
      i += 1
      runAfterContent(200, addNext)
    }

    addNext()
  }

  runPreambleThinking(() => {
    runThoughtParagraphs(() => {
      runBlocks(buildDesignDnaPlanBlocks(state.dnaArtifactId, { streaming: true }))
    })
  })
}
